import { useCallback, useMemo, useState } from 'react';

import { mergePDFs } from '@/utils';

import { SignatureStatus } from '@/types/transmissionReport.ts';
import { ListObjectResponse } from '@/types/vendorTransmissionReport.ts';

type UseViewAndDownloadReportsProps = {
  vendorReports?: ListObjectResponse[];
  pdfKey?: string;
  signatureStatus?: SignatureStatus;
  isCombinedPdf?: boolean;
  withVendorReports?: boolean;
};

/**
 * Custom hook to process, filter, and optionally merge vendor reports.
 * @param vendorReports List of vendor reports.
 * @param pdfKey Key to match specific PDF names.
 * @param signatureStatus Signature status of the report.
 * @param isCombinedPdf Whether the report is a combined PDF.
 * @param withVendorReports Whether to include other reports along with the report matching pdfKey.
 * @returns Processed reports, isProcessing flag, and a function to merge PDFs.
 */
const useViewAndDownloadReports = ({
  vendorReports = [],
  pdfKey = '',
  signatureStatus,
  isCombinedPdf = false,
  withVendorReports = false
}: UseViewAndDownloadReportsProps) => {
  const [isProcessing, setIsProcessing] = useState(false);

  const processedReports = useMemo(() => {
    if (!vendorReports.length) {
      return [];
    }

    const pdfKeyLower = pdfKey.trim().toLowerCase();

    return vendorReports
      .filter((report) => {
        const reportKey = report.key?.toLowerCase() ?? '';
        const isPdf = report.name?.toLowerCase().endsWith('.pdf');

        // Include reports that match the `pdfKey`, are PDFs, and have a "Signed" signature status when combined PDFs are enabled.
        if (signatureStatus === SignatureStatus.Signed && isCombinedPdf && pdfKeyLower) {
          return reportKey === pdfKeyLower && isPdf;
        }

        // Include PDFs that do not match the `pdfKey` (if provided) when the signature status is "Dismissed".
        if (signatureStatus === SignatureStatus.Dismissed) {
          return (!pdfKeyLower || reportKey !== pdfKeyLower) && isPdf;
        }
        // Include PDFs that match the `pdfKey` (if provided) when the signature status is "SignatureNotRequired".
        if (signatureStatus === SignatureStatus.SignatureNotRequired) {
          return (!pdfKeyLower || reportKey === pdfKeyLower) && isPdf;
        }

        // Include all PDFs if `withVendorReports` is true or if no `pdfKey` is provided.
        if (withVendorReports || !pdfKeyLower) {
          return isPdf;
        }

        // Include only the report that matches the `pdfKey` when none of the above conditions apply.
        return reportKey === pdfKeyLower;
      })
      .sort((a, b) => {
        // Prioritize reports matching pdfKey
        const aMatchesKey = a.key?.toLowerCase() === pdfKeyLower ? 1 : 0;
        const bMatchesKey = b.key?.toLowerCase() === pdfKeyLower ? 1 : 0;

        if (aMatchesKey !== bMatchesKey) {
          // Place reports matching `pdfKey` at the top.
          return bMatchesKey - aMatchesKey;
        }

        // If both or neither reports match the `pdfKey`, sort by the last modified date.
        const aDate = a.lastModified ? Date.parse(a.lastModified) : 0;
        const bDate = b.lastModified ? Date.parse(b.lastModified) : 0;

        if (aDate !== bDate) {
          // Sort by date, with older reports coming first.
          return aDate - bDate;
        }

        // If dates are identical, sort alphabetically by name.
        return a.name.localeCompare(b.name, undefined, { numeric: true, sensitivity: 'base' });
      });
  }, [vendorReports, pdfKey, signatureStatus, isCombinedPdf, withVendorReports]);

  const handleMergePDFs = useCallback(async (): Promise<Uint8Array | null> => {
    if (!processedReports.length) {
      return null;
    }

    const pdfUrls = processedReports
      .map((report) => report.pdfUrl)
      .filter((url): url is string => !!url);

    setIsProcessing(true);
    try {
      return await mergePDFs(pdfUrls);
    } catch (error) {
      console.error('Error during merging:', error);
      return null;
    } finally {
      setIsProcessing(false);
    }
  }, [processedReports]);

  return { processedReports, isProcessing, handleMergePDFs };
};

export default useViewAndDownloadReports;
